import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BookMarked, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function ResetPassword() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [pwd, setPwd] = useState("");
    const [pwd2, setPwd2] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (pwd.length < 6) {
            toast.error(t("auth.toast_password_short"));
            return;
        }
        if (pwd !== pwd2) {
            toast.error(t("reset.passwords_dont_match"));
            return;
        }
        setBusy(true);
        try {
            await api.post("/auth/password-reset/confirm", {
                token,
                new_password: pwd,
            });
            toast.success(t("reset.success_toast"));
            navigate("/acceder");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <main className="min-h-[calc(100vh-73px)] flex items-center justify-center px-5 py-12 md:py-16 bg-[var(--bg)]">
            <section className="w-full max-w-md">
                <Link
                    to="/"
                    className="flex items-center justify-center gap-2 text-[var(--ink)] mb-8"
                >
                    <BookMarked size={18} strokeWidth={1.5} />
                    <span
                        className="font-display text-2xl tracking-tight"
                        style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 32' }}
                    >
                        {t("nav.brand")}
                    </span>
                </Link>

                <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)] mb-3 text-center">
                    {t("reset.eyebrow")}
                </p>
                <h1
                    className="font-display text-4xl sm:text-5xl text-[var(--ink)] text-center mb-3 tracking-tight"
                    style={{ fontVariationSettings: '"wght" 600, "wdth" 85, "opsz" 96' }}
                >
                    {t("reset.title")}
                </h1>
                <p className="text-center text-sm text-[var(--ink-muted)] mb-10 max-w-xs mx-auto">
                    {t("reset.lead")}
                </p>

                {!token ? (
                    <p
                        data-testid="reset-no-token"
                        className="text-center text-sm text-[var(--danger)]"
                    >
                        {t("reset.no_token")}
                    </p>
                ) : (
                    <form onSubmit={submit} data-testid="reset-form" className="space-y-6">
                        <div>
                            <label className="block text-xs uppercase tracking-widest text-[var(--ink-muted)] mb-2">
                                {t("reset.new_password")}
                            </label>
                            <input
                                data-testid="reset-pwd-input"
                                type="password"
                                required
                                minLength={6}
                                value={pwd}
                                onChange={(e) => setPwd(e.target.value)}
                                className="w-full bg-transparent border-b border-[var(--ink)] py-2 text-lg text-[var(--ink)] focus:outline-none focus:border-[var(--accent)] transition-colors"
                            />
                        </div>
                        <div>
                            <label className="block text-xs uppercase tracking-widest text-[var(--ink-muted)] mb-2">
                                {t("reset.confirm_password")}
                            </label>
                            <input
                                data-testid="reset-pwd2-input"
                                type="password"
                                required
                                minLength={6}
                                value={pwd2}
                                onChange={(e) => setPwd2(e.target.value)}
                                className="w-full bg-transparent border-b border-[var(--ink)] py-2 text-lg text-[var(--ink)] focus:outline-none focus:border-[var(--accent)] transition-colors"
                            />
                        </div>
                        <button
                            data-testid="reset-submit-btn"
                            type="submit"
                            disabled={busy}
                            className="w-full inline-flex items-center justify-center gap-2 bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] disabled:opacity-50 transition-colors px-8 py-3.5 text-sm uppercase tracking-widest"
                        >
                            {busy && <Loader2 size={14} className="animate-spin" />}
                            {t("reset.submit")}
                        </button>
                    </form>
                )}
            </section>
        </main>
    );
}
